
import React, { useRef, useState, useEffect } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Environment } from "@react-three/drei";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AIBall from "./AIBall";
import AIPyramid from "./AIPyramid";
import AIGlowingSphere from "./AIGlowingSphere";
import AIGlowingSphere2 from "./AIGlowingSphere2";
import AIEnhancedBall from "./AIEnhancedBall";

// Define the assistant types
type AssistantType = "futuristicBall" | "holographicSphere" | "glowingSphere" | "glowingSphere2" | "enhancedBall";

// Get the assistant component based on type
const getAssistantComponent = (type: AssistantType, isTalking: boolean) => {
  switch (type) {
    case "futuristicBall":
      return <AIBall isTalking={isTalking} />;
    case "holographicSphere":
      return <AIPyramid isTalking={isTalking} />;
    case "glowingSphere":
      return <AIGlowingSphere isTalking={isTalking} />;
    case "glowingSphere2":
      return <AIGlowingSphere2 isTalking={isTalking} />;
    case "enhancedBall":
      return <AIEnhancedBall isTalking={isTalking} />;
    default:
      return <AIBall isTalking={isTalking} />;
  }
};

interface ThreeAssistantProps {
  onAskQuestion?: (question: string) => void;
  isResponding?: boolean;
  className?: string;
  selectorOnly?: boolean;
  hideSelector?: boolean;
  hideForm?: boolean;
  assistantType?: string;
  onAssistantChange?: (type: string) => void;
  containerSize?: number;
  hideRespondingLabel?: boolean; // New prop
}

const ThreeAssistant: React.FC<ThreeAssistantProps> = ({
  onAskQuestion,
  isResponding = false,
  className = "",
  selectorOnly = false,
  hideSelector = false,
  hideForm = false,
  assistantType = "futuristicBall",
  onAssistantChange,
  containerSize = 400,
  hideRespondingLabel = false, // Default to false for backward compatibility
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [question, setQuestion] = useState("");
  const [localAssistantType, setLocalAssistantType] = useState<AssistantType>("futuristicBall");
  
  useEffect(() => {
    setIsMounted(true);
    if (assistantType) {
      setLocalAssistantType(assistantType as AssistantType);
    }
    return () => setIsMounted(false);
  }, [assistantType]);

  const handleAskQuestion = () => {
    if (!question.trim()) {
      toast({
        title: "Empty Question",
        description: "Please enter a question to ask.",
        variant: "destructive",
      });
      return;
    }
    
    if (onAskQuestion) {
      onAskQuestion(question);
    } else {
      toast({
        title: "Question received",
        description: `You asked: ${question}`,
      });
    }
    
    setQuestion("");
  };

  const handleAssistantChange = (value: string) => {
    setLocalAssistantType(value as AssistantType);
    
    if (onAssistantChange) {
      onAssistantChange(value);
    } else {
      let displayName = "Futuristic Ball";
      if (value === "holographicSphere") displayName = "Holographic Sphere";
      if (value === "glowingSphere") displayName = "Glowing Sphere";
      if (value === "glowingSphere2") displayName = "Glowing Sphere 2";
      if (value === "enhancedBall") displayName = "Enhanced Ball";
      
      toast({
        title: "Assistant Style Changed",
        description: `Switched to ${displayName} assistant.`,
      });
    }
  };

  // Don't render the Canvas on the server
  if (!isMounted) {
    return null;
  }

  // If we only want to show the selector
  if (selectorOnly) {
    return (
      <div className="flex justify-center">
        <Select value={localAssistantType} onValueChange={handleAssistantChange}>
          <SelectTrigger className="w-[250px]">
            <SelectValue placeholder="Select assistant style" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="futuristicBall">Futuristic Ball</SelectItem>
            <SelectItem value="enhancedBall">Enhanced Ball</SelectItem>
            <SelectItem value="holographicSphere">Holographic Sphere</SelectItem>
            <SelectItem value="glowingSphere">Glowing Sphere</SelectItem>
            <SelectItem value="glowingSphere2">Glowing Sphere 2</SelectItem>
          </SelectContent>
        </Select>
      </div>
    );
  }

  // Set the canvas style with exact dimensions to ensure it's rendered at 1:1 ratio
  const canvasStyle = {
    width: `${containerSize}px`, 
    height: `${containerSize}px`
  };

  return (
    <div className={`flex flex-col w-full h-full ${className}`}>
      {!hideSelector && (
        <div className="flex justify-end mb-2">
          <Select value={localAssistantType} onValueChange={handleAssistantChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select style" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="futuristicBall">Futuristic Ball</SelectItem>
              <SelectItem value="enhancedBall">Enhanced Ball</SelectItem>
              <SelectItem value="holographicSphere">Holographic Sphere</SelectItem>
              <SelectItem value="glowingSphere">Glowing Sphere</SelectItem>
              <SelectItem value="glowingSphere2">Glowing Sphere 2</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      
      <div style={canvasStyle} className="relative overflow-hidden">
        <Canvas 
          camera={{ position: [0, 0, 5], fov: 50 }}
          style={{ width: '100%', height: '100%' }}
        >
          <ambientLight intensity={0.8} />
          <pointLight position={[10, 10, 10]} intensity={1} />
          <pointLight position={[-10, -10, -10]} intensity={0.5} color="#0066ff" />
          {getAssistantComponent(localAssistantType as AssistantType, isResponding)}
          <OrbitControls enableZoom={false} enablePan={false} />
          <Environment preset="city" />
        </Canvas>
        {isResponding && !hideRespondingLabel && (
          <div className="absolute top-4 left-0 right-0 mx-auto w-max bg-primary/10 backdrop-blur-sm rounded-lg px-4 py-2 text-primary animate-pulse">
            Responding...
          </div>
        )}
      </div>
      
      {!hideForm && (
        <div className="flex flex-col gap-4 p-4 bg-card rounded-lg mt-4">
          <h3 className="text-lg font-semibold">Ask your assistant</h3>
          <div className="flex gap-2">
            <input
              type="text"
              className="flex-1 px-4 py-2 border rounded-md"
              placeholder="Type your question here..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAskQuestion()}
            />
            <Button 
              onClick={handleAskQuestion}
              disabled={isResponding}
            >
              Ask
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThreeAssistant;
